import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as sup from "superstruct";

import { Form } from "@/modules/form/components/Form/Form";
import { Label } from "@/modules/form/components/Label/Label";
import { TextArea } from "@/modules/form/components/TextArea/TextArea";
import { notify } from "@/modules/notify/notify";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { CompleteSubtaskMutationPayload } from "@/modules/tasks/api/mutations/completeSubtaskMutation";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { useUserDataContext } from "@/modules/user/hooks/useUserDataContext";

import { createSubtaskFormStyles } from "../styles/createSubtaskFormStyles";

import { TaskStatusToggler } from "./TaskStatusToggler";
import { TutorialLink } from "./TutorialLink";

const translationNamespace = "pages/SubtaskDetailsPage";

const commentStruct = sup.object({
  comment: sup.string(),
});

type CommentStruct = sup.Infer<typeof commentStruct>;

type SubtaskFormProps = {
  description: string;
  tutorialId: string | undefined;
  formId: string;
  completeSubtaskMutate: (param: CompleteSubtaskMutationPayload) => void;
};

export const SubtaskForm: React.FC<SubtaskFormProps> = ({
  tutorialId,
  description,
  formId,
  completeSubtaskMutate,
}) => {
  const classes = createSubtaskFormStyles(useTheme());
  const { t } = useTranslation(translationNamespace);
  const { currentGroup } = useUserDataContext();
  const params = authenticatedRouting.useRouteParams("undoneSubtaskDetails");

  const handleSubmit = useCallback(
    ({ comment }: CommentStruct) => {
      if (currentGroup && params) {
        completeSubtaskMutate({
          comment,
          userGroupId: currentGroup.id,
          subtaskId: params.id,
        });
      } else {
        notify.alert.error(t("Something went wrong..."));
      }
    },
    [t, completeSubtaskMutate, params, currentGroup]
  );

  return (
    <>
      {description && (
        <>
          <Typography variant="contentMedium" className={classes.header}>
            {t("Description")}
          </Typography>
          <Typography variant="contentMedium" className={classes.text}>
            {description}
          </Typography>
        </>
      )}
      {tutorialId && (
        <TutorialLink
          to={authenticatedRouting.buildUrl("tutorialDetails", {
            params: {
              id: tutorialId,
              step: "1",
            },
          })}
        />
      )}
      <Form
        defaultValues={{
          comment: "",
        }}
        id={formId}
        struct={commentStruct}
        onValidSubmit={handleSubmit}
        className={classes.form}
      >
        {({ register }) => (
          <>
            <Label htmlFor="comment" className={classes.label}>
              {t("Your comment")}
            </Label>
            <TextArea
              id="comment"
              {...register("comment")}
              textAreaClassName={classes.textArea}
              placeholder={t("Tap to write a message…")}
            />
          </>
        )}
      </Form>
      <TaskStatusToggler done={false} formId={formId} />
    </>
  );
};
